import { useMemo } from 'react';
import DeviceManager from '@/components/util/deviceManager';

const getHeight = ({
  mobilePosition,
  popupOpen,
  popupHeight,
  choicePopupOpen,
  navigationSettings,
  navigationSharingPopupOpen,
  actionPathFinished,
}) => {
  if (mobilePosition > 1) {
    const maxPixels = popupHeight + 102;

    return popupOpen && popupHeight && maxPixels < window.innerHeight ? `${maxPixels}px` : '95%';
  }

  if (mobilePosition < 1) {
    if (!popupOpen && !choicePopupOpen) {
      return '15%';
    }

    return '31%';
  }

  if (choicePopupOpen && mobilePosition === 1) {
    return 'calc(31% + 88px)';
  }

  if (choicePopupOpen && navigationSettings?.detailedRouteEnabled) {
    return '50%';
  }

  if (navigationSharingPopupOpen && actionPathFinished) {
    return '31%';
  }

  return '40%';
};

export const useStyles = ({
  isBackgroundVisible,
  navigationSharingPopupOpen,
  actionPathFinished,
  choicePopupOpen,
  mobilePosition,
  popupHeight,
  navigationSettings,
  popupOpen,
  sharePopupOpen,
  selectedCategory,
}) => {
  const mobileHeight = useMemo(
    () =>
      getHeight({
        actionPathFinished,
        choicePopupOpen,
        mobilePosition,
        navigationSettings,
        navigationSharingPopupOpen,
        popupHeight,
        popupOpen,
      }),
    [
      actionPathFinished,
      choicePopupOpen,
      mobilePosition,
      navigationSettings,
      navigationSharingPopupOpen,
      popupHeight,
      popupOpen,
    ],
  );

  const presentationStyle = useMemo(
    () => ({
      width: '100%',
      height: {
        // eslint-disable-next-line no-nested-ternary
        xs: DeviceManager.isMobile()
          ? isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished)
            ? mobileHeight
            : '0px'
          : '100%',
        sm: '100%',
      },
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: '76px',
      alignSelf: 'center',
      position: { xs: DeviceManager.isMobile() ? 'fixed' : 'relative', sm: 'relative' },
      bottom: {
        xs: DeviceManager.isMobile()
          ? isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished)
            ? 0
            : '-100px'
          : 0,
        sm: 0,
      },
      borderRadius: { xs: DeviceManager.isMobile() ? '20px 20px 0 0' : 0, sm: 0 },
      background: { xs: DeviceManager.isMobile() ? '#F7F7F7' : 'transparent', sm: 'transparent' },
      px: {
        xs: selectedCategory ? '0px' : '20px',
        sm: popupOpen || sharePopupOpen || choicePopupOpen ? '0px' : '151px',
        lg: popupOpen || sharePopupOpen || choicePopupOpen ? '0px' : '151px',
        xl: popupOpen || sharePopupOpen || choicePopupOpen ? '0px' : '151px',
      },
      marginBottom: { sm: '110px' },
      visibility: {
        xs: DeviceManager.isMobile()
          ? isBackgroundVisible || (navigationSharingPopupOpen && actionPathFinished)
            ? 'visible'
            : 'hidden'
          : 'unset',
        sm: 'unset',
      },
      maxHeight: {
        xs: DeviceManager.isMobile() ? '95%' : '100%',
        sm: '100%',
      },
      transition: 'height 0.3s',
    }),
    [
      actionPathFinished,
      choicePopupOpen,
      isBackgroundVisible,
      mobileHeight,
      navigationSharingPopupOpen,
      popupOpen,
      sharePopupOpen,
    ],
  );

  const mainWrapperStyle = useMemo(
    () => ({
      position: 'absolute',
      width: '100%',
      height: '100dvh',
      background: 'transparent',
      zIndex: 1200,
      top: 0,
      pointerEvents: isBackgroundVisible ? 'auto' : 'none !important',
      overflowY: {
        xs: DeviceManager.isMobile() ? 'hidden' : mobilePosition === 0 || popupOpen ? 'hidden' : '',
        sm: mobilePosition === 0 || popupOpen ? 'hidden' : '',
      },
      overflowX: 'hidden',
      justifyContent: 'space-between',
    }),
    [isBackgroundVisible, mobilePosition, popupOpen],
  );

  const whiteLineStyle = useMemo(
    () => ({
      background: '#fff',
      width: '47px',
      height: '6px',
      borderRadius: '26px',
      display: { xs: DeviceManager.isMobile() ? 'flex' : 'none', sm: 'none' },
    }),
    [],
  );
  const whiteLineWrapperStyle = useMemo(
    () => ({
      background: 'transparent',
      width: '57px',
      height: '24px',
      // borderRadius: '26px',
      position: 'absolute',
      top: '-20px',
      alignItems: 'center',
      justifyContent: 'center',
      display: { xs: DeviceManager.isMobile() ? 'flex' : 'none', sm: 'none' },
      zIndex: 9999,
    }),
    [],
  );

  return { presentationStyle, mainWrapperStyle, whiteLineStyle, whiteLineWrapperStyle };
};

export const useCategoriesStyles = () => {
  const categoriesGridWrapperStyle = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'flex-start',
      alignContent: 'flexStart',
      rowGap: '10px',
      alignSelf: 'stretch',
      flexWrap: 'wrap',
      overflowY: 'auto',
      zIndex: 10,
      position: 'relative',
      paddingBottom: '60px',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': {
        width: '6px',
        backgroundColor: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'transparent',
        borderRadius: '3px',
        transition: 'background-color 0.2s ease',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(189, 189, 189, 0.7)',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(255, 255, 255, 0.3)',
        },
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(189, 189, 189, 0.9)',
      },
    }),
    [],
  );

  const gridItemInnerStyle = useMemo(
    () => ({
      overflow: 'hidden',
      gap: '6px',
      alignItems: 'center',
      justifyContent: 'start',
      p: { xs: DeviceManager.isMobile() ? '10px' : '14px 14px 0px 14px', lg: '14px 14px 0px 14px' },
      width: '154px',
      height: '94px',
      display: 'flex !important',
      borderRadius: '8px',
      border: '1px solid #EAECF0',
      background: '#FFF',
      cursor: 'pointer',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }),
    [],
  );

  const gridItemTitleStyle = useMemo(
    () => ({
      fontSize: {
        xs: '14px',
        sm: '14px',
        md: '15px',
        lg: '14px',
        xl: '14px',
      },
      fontWeight: { xs: 400, sm: 500 },
      fontFamily: 'Inter, Heebo, sans-serif',
      textAlign: 'center',
      whiteSpace: 'normal',
      width: '100%',
      overflow: 'hidden',
      wordWrap: 'break-word',
      hyphens: 'auto',
      p: {
        xs: '0 8px',
        sm: '0 6px',
        md: '0 8px',
        lg: '0 6px',
        xl: '0 10px',
      },
      lineHeight: {
        xs: 'normal',
        sm: 'normal',
        md: '1.2',
        lg: 'normal',
        xl: '1.3',
      },
      maxWidth: '100%',
      boxSizing: 'border-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    }),
    [],
  );
  return { categoriesGridWrapperStyle, gridItemInnerStyle, gridItemTitleStyle };
};

export const ZoomButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  fontWeight: 500, // font-medium
  transition: 'all 0.2s ease-in-out', // transition-colors approximation
  outline: 'none',
  '&:focus-visible': {
    outline: 'none',
  },
  '&:disabled': {
    pointerEvents: 'none',
    opacity: 0.5,
  },
  '& svg': {
    pointerEvents: 'none',
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  backgroundColor: '#fff',
  height: 40,
  width: 40,
};
